import { useTranslation } from 'module/hooks/use-translation';
import { logger } from 'module/logger';
import { Breadcrumb } from 'module/ui/components/atoms/breadcrumb/breadcrumb';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { PageLoading } from 'module/ui/components/atoms/loading';
import { Argon } from 'module/ui/components/templates/argon';
// import { faPlay } from '@fortawesome/pro-solid-svg-icons';
import { faFloppyDisk } from '@fortawesome/pro-solid-svg-icons';
import { Link, useParams, useNavigate } from '@rocketlane/rocket-router';
import { Button } from '@rocketlane/ui';
import { CompanyPageTemplateUiState } from 'api/company-page-template/types';
import {
  PageType,
  SourceType,
  OperationPayload,
} from 'api/page-template/types';
import {
  useGetCompanyPageTemplateQuery,
  useUpdateCompanyPageTemplateMutation,
  useCreateCompanyPageTemplateMutation,
} from 'queries/company-page-template';
import { useState, useCallback } from 'react';
import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { ROUTE_PATHS } from 'routes/route-paths';
import { FlexWrapperWithGutter } from 'components/common/styles';
// import { PreviewButton } from 'components/organisms/document-editor/extensions/custom-section/ui/shared/section-styles';
import { CompanyTemplatePageV2 } from 'components/organisms/pages-template/company-page/company-page-template-v2';
import {
  defaultSections,
  DEFAULT_SECTION_IDS,
  defaultContent,
} from 'components/organisms/pages-template/company-page/company-page-util';
import { CompanyPageLevelPreview } from './company-page-preview';
import { CompanyDocumumentTitle } from './template-title';
import type { CompanyPageTemplatePermissions } from 'types/nitro-pages';

const COMPANY_PAGE_ROUTE = 'company-pages';

export type { CompanyPageTemplatePermissions };

export interface CompanyPageTemplateV2PageProps {
  companyPageTemplatePermissions?: CompanyPageTemplatePermissions;
}
export const CompanyPageTemplateV2Page = ({
  companyPageTemplatePermissions,
}: CompanyPageTemplateV2PageProps = {}) => {
  const companyPageTemplateTitle = useTranslation(
    'company_page_templates.title',
  );
  const { companyPageTemplateId } = useParams<{
    companyPageTemplateId: string;
  }>();

  const [isPreviewOpen, _setIsPreviewOpen] = useState(false);

  const parsedCompanyPageTemplateId = companyPageTemplateId
    ? parseInt(companyPageTemplateId, 10)
    : -1;

  const navigate = useNavigate();
  const isNew =
    companyPageTemplateId != null && companyPageTemplateId === 'new';
  const newTemplateString = useTranslation('new_template');

  const getDefaultData = useCallback(() => {
    return {
      templateId: parsedCompanyPageTemplateId,
      sourceType: SourceType.Company as const,
      pageType: PageType.Company as const,
      templateName: newTemplateString,
      changedSections: defaultSections,
      tiptapJson: defaultContent,
      existingSectionsIds: DEFAULT_SECTION_IDS,
      htmlPayload: {},
    };
  }, [newTemplateString, parsedCompanyPageTemplateId]);
  const [templateData, setTemplateData] =
    useState<CompanyPageTemplateUiState>();
  const [componentKey, setComponentKey] = useState(0);

  const { mutateAsync: createCompanyPageTemplate, isPending: isSaving } =
    useCreateCompanyPageTemplateMutation();

  const updateTemplateState = useCallback(
    (payload: Partial<CompanyPageTemplateUiState>) => {
      setTemplateData((previous) => {
        const newData =
          previous != null
            ? { ...previous, ...payload }
            : { ...getDefaultData(), ...payload };

        // Ensure filters is always an empty object for UI
        return { ...newData, filters: {} };
      });
    },
    [getDefaultData],
  );

  const {
    data: template,
    isLoading: isLoadingCompanyPageTemplate,
    isError: isErrorCompanyPageTemplate,
  } = useGetCompanyPageTemplateQuery(parsedCompanyPageTemplateId, {
    enabled: !isNew && parsedCompanyPageTemplateId > 0,
  });

  const { mutateAsync: updateCompanyPageTemplate, isPending: isUpdating } =
    useUpdateCompanyPageTemplateMutation();

  useEffect(() => {
    if (isNew) {
      updateTemplateState(getDefaultData());
      setComponentKey((prev) => prev + 1);
    } else if (template && !isLoadingCompanyPageTemplate) {
      updateTemplateState({
        htmlPayload: template?.htmlPayload,
        tiptapJson: template?.tiptapJson,
        pageType: template?.pageType,
        sourceType: SourceType.Company as const,
        templateId: template?.templateId,
        templateName: template?.templateName,
        changedSections: [],
        existingSectionsIds: template?.templateSectionIds,
      });
      setComponentKey((prev) => prev + 1);
    }
  }, [
    template,
    updateTemplateState,
    isNew,
    isLoadingCompanyPageTemplate,
    getDefaultData,
    newTemplateString,
  ]);

  const handleTitleChange = (newTitle: string) => {
    updateTemplateState({ templateName: newTitle });
  };

  const transformTemplateData = useCallback(
    (templateData: CompanyPageTemplateUiState) => {
      const operations: OperationPayload[] =
        (templateData?.operations?.length ?? 0) > 0
          ? templateData.operations!
          : (() => {
              const built: OperationPayload[] = [];

              templateData?.changedSections?.forEach((section) => {
                if (isNew) {
                  built.push({ operation: 'POST', payload: section });
                } else if (
                  section?.attrs?.templateSectionId &&
                  templateData?.existingSectionsIds?.includes(
                    section?.attrs?.templateSectionId,
                  )
                ) {
                  built.push({ operation: 'PUT', payload: section });
                } else {
                  built.push({ operation: 'POST', payload: section });
                }
              });

              return built;
            })();

      const {
        sourceType,
        changedSections: _changedSections,
        existingSectionsIds: _existingSectionsIds,
        operations: _operations,
        ...restTemplateData
      } = templateData;

      return {
        ...restTemplateData,
        sourceType: SourceType.Meeting as const, // API expects Meeting sourceType
        operations,
        filters: {}, // Empty filters for API compatibility
      };
    },
    [isNew],
  );

  const handleTemplateSave = useCallback(async () => {
    try {
      if (templateData == null) {
        return;
      }
      if (isNew) {
        createCompanyPageTemplate(transformTemplateData(templateData)).then(
          (response) => {
            const templateId = response?.templateId;

            updateTemplateState({
              templateId,
            });
            navigate(
              `/${ROUTE_PATHS.TEMPLATES}/${COMPANY_PAGE_ROUTE}/${templateId}`,
            );
          },
        );
      } else {
        updateCompanyPageTemplate(transformTemplateData(templateData)).then(
          (response) => {
            updateTemplateState({
              ...response,
              operations: [],
              changedSections: [],
              sourceType: SourceType.Company as const, // Convert back to Company for UI
              filters: {}, // Remove filters from UI state
            });
          },
        );
      }
    } catch (error) {
      logger.error('Error saving company page template', { error });
    }
  }, [
    isNew,
    createCompanyPageTemplate,
    transformTemplateData,
    templateData,
    updateTemplateState,
    navigate,
    updateCompanyPageTemplate,
  ]);

  return (
    <>
      {!isNew && isLoadingCompanyPageTemplate ? (
        <PageLoading />
      ) : !isNew && isErrorCompanyPageTemplate ? (
        <div>
          <FormattedMessage id="company_page_templates.error.loading" />
        </div>
      ) : isPreviewOpen && templateData ? (
        <CompanyPageLevelPreview />
      ) : templateData ? (
        <Argon
          key={componentKey}
          breadcrumbs={
            <>
              <Breadcrumb.Item>
                <Link
                  to={`/${ROUTE_PATHS.TEMPLATES}/${ROUTE_PATHS.COMPANY_PAGE_TEMPLATE_LIST}`}
                >
                  <FormattedMessage id="company_page_templates.title" />
                </Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>&nbsp;</Breadcrumb.Item>
            </>
          }
          className="company-page-template-page"
          documentTitle={companyPageTemplateTitle}
          title={
            <CompanyDocumumentTitle
              value={templateData.templateName ?? ''}
              onChange={handleTitleChange}
            />
          }
          actions={{}}
          pageActions={
            <FlexWrapperWithGutter>
              {/* <PreviewButton
                variant="secondary"
                onClick={() => setIsPreviewOpen(true)}
              >
                <FontAwesomeIcon icon={faPlay} />
              </PreviewButton> */}
              {companyPageTemplatePermissions?.canWrite !== false && (
                <Button
                  variant="primary"
                  onClick={handleTemplateSave}
                  leftIcon={<FontAwesomeIcon icon={faFloppyDisk} />}
                  loading={isSaving || isUpdating}
                >
                  <FormattedMessage id="save" />
                </Button>
              )}
            </FlexWrapperWithGutter>
          }
          content={
            <CompanyTemplatePageV2
              key={`template-${componentKey}`}
              templateData={templateData}
              onTemplateChange={updateTemplateState}
              previousTiptapJson={isNew ? undefined : template?.tiptapJson}
            />
          }
        />
      ) : (
        <></>
      )}
    </>
  );
};
